if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (!this.submitBtn || this.submitBtn.disabled) return;

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let blockedDistinctLimit = false;

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const variantId = Number(formData.get('id'));
        const maxDistinct = Number(theme.settings.cartMaxDistinctItems) || 10;
        const giftBagsEl = this.form.querySelector('gift-bags');
        const pendingBags = giftBagsEl && typeof giftBagsEl.getPendingItems === 'function'
          ? giftBagsEl.getPendingItems()
          : [];

        let response;
        let data;

        if (pendingBags.length) {
          const items = [{
            id: variantId,
            quantity: Number(formData.get('quantity') || 1)
          }];
          const sellingPlan = formData.get('selling_plan');
          if (sellingPlan) items[0].selling_plan = sellingPlan;

          const properties = {};
          formData.forEach((value, key) => {
            const match = key.match(/^properties\[(.*)\]$/);
            if (match && value !== '') properties[match[1]] = value;
          });
          if (Object.keys(properties).length) items[0].properties = properties;

          pendingBags.forEach((bag) => items.push(bag));

          const existingIds = new Set(oldCartData.items.map((item) => item.variant_id));
          const newDistinctCount = items.reduce((count, item) => (
            existingIds.has(item.id) ? count : count + 1
          ), 0);

          if (oldCartData.items.length + newDistinctCount > maxDistinct) {
            blockedDistinctLimit = true;
            await this.handleDistinctItemsLimit(maxDistinct);
            return;
          }

          response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname
            })
          });
          data = await response.json();
          if (giftBagsEl && typeof giftBagsEl.reset === 'function' && response.ok && !data.status) {
            giftBagsEl.reset();
          }
        } else {
          const alreadyInCart = oldCartData.items.some((item) => item.variant_id === variantId);

          if (!alreadyInCart && oldCartData.items.length >= maxDistinct) {
            blockedDistinctLimit = true;
            await this.handleDistinctItemsLimit(maxDistinct);
            return;
          }

          response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
          data = await response.json();
        }
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const addedVariantId = data.variant_id || variantId;
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === addedVariantId
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: addedVariantId,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: addedVariantId
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.removeAttribute('aria-disabled');
        if (blockedDistinctLimit) {
          this.submitBtn.classList.remove('is-loading');
        } else {
          this.submitBtn.classList.add('is-success');
          setTimeout(() => {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.classList.remove('is-success');
          }, 1400);
        }
      }
    }

    /**
     * Blocks adding a new line item past the distinct-items limit and shows the cart alert.
     * @param {number} maxDistinct - Maximum distinct line items allowed.
     */
    async handleDistinctItemsLimit(maxDistinct) {
      const quickAddDrawer = this.closest('quick-add-drawer');
      if (quickAddDrawer && typeof quickAddDrawer.close === 'function') {
        quickAddDrawer.close();
      }

      const scope = this.cartDrawer || document;
      scope.querySelectorAll('.cart-item__error').forEach((el) => {
        if (el.id !== 'cart-distinct-items-error') el.hidden = true;
      });

      if (this.cartDrawer) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        // Wait a tick so the drawer (and cart-items) are visible before revealing the alert.
        await new Promise((resolve) => {
          requestAnimationFrame(() => setTimeout(resolve, 50));
        });
      }

      ProductForm.showDistinctItemsLimitAlert(maxDistinct, this.cartDrawer);
    }

    /**
     * Shows the cart-level distinct-items limit alert.
     * @param {number} maxDistinct - Maximum distinct line items allowed.
     * @param {Element} [scope] - Prefer alert inside this root (e.g. cart-drawer).
     */
    static showDistinctItemsLimitAlert(maxDistinct, scope) {
      const root = scope || document;
      const alertEl = root.querySelector('#cart-distinct-items-error')
        || document.getElementById('cart-distinct-items-error');
      if (!alertEl) return;

      const body = alertEl.querySelector('.js-quantity-limit-body');
      const template = theme.strings.distinctItemsLimitBodyHtml;
      if (body && template) {
        body.innerHTML = template.replace(/\[quantity\]/g, String(maxDistinct));
      }

      alertEl.dataset.quantityMax = String(maxDistinct);
      alertEl.hidden = false;

      // Keep the alert in view inside the drawer.
      alertEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
